/**
 * TypeScript types for preferences questionnaire domain.
 *
 * Belongs here: shared types.
 * Does not belong here: React components.
 */
import type {
  Question,
  AllocationAnswersMap,
  SubmitQuestionInput,
  QuestionnaireQuestionApi,
  PreferenceGroupRefApi,
  QuestionOptionFundItem
} from '../../shared/types/questionnaire.types'
import type { QuestionnaireStatus } from '../../shared/types/questionnaire.types'

export type PreferencesAnswersMap = AllocationAnswersMap

/** Full questionnaire vs edit-one-preference-type route. */
export enum PreferencesQuestionnaireScope {
  All = 'all',
  Single = 'single'
}

/** Backend questionType on preference details questions. Only SEGMENT_INTRO drives FE branching. */
export enum PreferenceQuestionType {
  CATEGORY_BASED_STATIC = 'CATEGORY_BASED_STATIC',
  GROUP_BASED_STATIC = 'GROUP_BASED_STATIC',
  CATEGORY_BASED_DYNAMIC = 'CATEGORY_BASED_DYNAMIC',
  SEGMENT_INTRO = 'SEGMENT_INTRO',
  GROUP_MULTI_SELECT_CATEGORY = 'GROUP_MULTI_SELECT_CATEGORY'
}

export interface FundPreferenceType {
  id: string
  name: string
  fundCount: number
  isNewPreference?: boolean
  portfolioPercent?: number
  icon?: string
  questionIds: string[]
  fundList?: PreferenceFundItem[]
  /** Optional disclaimer shown in the preference type header when viewing this group. */
  disclaimer?: string
  groupName?: string | null
  groups?: PreferenceGroupRefApi[]
}

/**
 * Preference / segment / category ids from preference details.
 * Backend group & segment enums are numeric; API preference ids use these string names.
 */
export enum PreferenceTypeId {
  // Groups (string names as returned on preferenceList[].id)
  HIGH_RISK = 'HIGH_RISK',
  MEDIUM_RISK = 'MEDIUM_RISK',
  LOW_RISK = 'LOW_RISK',
  SILVER_AND_GOLD = 'SILVER_AND_GOLD',
  GLOBAL_US = 'GLOBAL_US',
  GLOBAL_NON_CORE = 'GLOBAL_NON_CORE',
  HYBRID = 'HYBRID',
  DEBT = 'DEBT',
  OTHER = 'OTHER',
  INDEX = 'INDEX',
  SECTORAL = 'SECTORAL',
  DEBT_AND_ARBITRAGE = 'DEBT_AND_ARBITRAGE',
  RETIREMENT = 'RETIREMENT',
  CHILDREN = 'CHILDREN',
  NEW_INVESTMENT = 'NEW_INVESTMENT',
  EMERGENCY = 'EMERGENCY',

  // Segments
  CORE = 'CORE',
  SATELLITE = 'SATELLITE',
  STRATEGIC = 'STRATEGIC',
  GOAL_BASED = 'GOAL_BASED',
  OTHERS = 'OTHERS',

  // Categories
  EQUITY_SAVINGS = 'EQUITY_SAVINGS',
  CONSERVATIVE_ALLOCATION = 'CONSERVATIVE_ALLOCATION',
  DYNAMIC_ASSET_ALLOCATION = 'DYNAMIC_ASSET_ALLOCATION',
  FUND_OF_FUNDS = 'FUND_OF_FUNDS',
  MULTI_ASSET_ALLOCATION = 'MULTI_ASSET_ALLOCATION',
  AGGRESSIVE_ALLOCATION = 'AGGRESSIVE_ALLOCATION',
  BALANCED_ALLOCATION = 'BALANCED_ALLOCATION',
  ARBITRAGE_FUND = 'ARBITRAGE_FUND',
  SECTOR_PRECIOUS_METALS = 'SECTOR_PRECIOUS_METALS',
  EQUITY_CONSUMPTION = 'EQUITY_CONSUMPTION',
  FLEXI_CAP = 'FLEXI_CAP',
  MID_CAP = 'MID_CAP',
  MULTI_CAP = 'MULTI_CAP',
  LARGE_CAP = 'LARGE_CAP',
  POPULAR_EQUITY = 'POPULAR_EQUITY',
  POPULAR_INDEX = 'POPULAR_INDEX',
  POPULAR_HYBRID = 'POPULAR_HYBRID',
  DIVIDEND_YIELD = 'DIVIDEND_YIELD',
  EQUITY_INFRASTRUCTURE = 'EQUITY_INFRASTRUCTURE',
  SECTOR_FINANCIAL_SERVICES = 'SECTOR_FINANCIAL_SERVICES',
  INDEX_FUNDS = 'INDEX_FUNDS',
  GLOBAL_OTHER = 'GLOBAL_OTHER',
  SMALL_CAP = 'SMALL_CAP',
  EQUITY_OTHER = 'EQUITY_OTHER',
  SECTOR_TECHNOLOGY = 'SECTOR_TECHNOLOGY',
  ELSS_TAX_SAVINGS = 'ELSS_TAX_SAVINGS',
  CONTRA = 'CONTRA',
  VALUE_AND_CONTRA = 'VALUE_AND_CONTRA',
  SECTOR_HEALTHCARE = 'SECTOR_HEALTHCARE',
  LARGE_AND_MID_CAP = 'LARGE_AND_MID_CAP',
  EQUITY_ESG = 'EQUITY_ESG',
  VALUE = 'VALUE',
  FOCUSED_FUND = 'FOCUSED_FUND',
  SECTOR_ENERGY = 'SECTOR_ENERGY',
  SECTOR_FMCG = 'SECTOR_FMCG',
  MONEY_MARKET = 'MONEY_MARKET',
  FLOATING_RATE = 'FLOATING_RATE',
  ULTRA_SHORT_DURATION = 'ULTRA_SHORT_DURATION',
  CORPORATE_BOND = 'CORPORATE_BOND',
  DYNAMIC_BOND = 'DYNAMIC_BOND',
  MEDIUM_TO_LONG_DURATION = 'MEDIUM_TO_LONG_DURATION',
  LOW_DURATION = 'LOW_DURATION',
  INDEX_FUNDS_FIXED_INCOME = 'INDEX_FUNDS_FIXED_INCOME',
  BANKING_AND_PSU = 'BANKING_AND_PSU',
  LONG_DURATION = 'LONG_DURATION',
  GOVERNMENT_BOND = 'GOVERNMENT_BOND',
  SHORT_DURATION = 'SHORT_DURATION',
  MEDIUM_DURATION = 'MEDIUM_DURATION',
  CREDIT_RISK = 'CREDIT_RISK',
  OTHER_BOND = 'OTHER_BOND',
  TEN_YR_GOVERNMENT_BOND = 'TEN_YR_GOVERNMENT_BOND',
  LIQUID = 'LIQUID',
  OVERNIGHT = 'OVERNIGHT',
  INDEX_AUTO = 'INDEX_AUTO',
  INDEX_BANKS = 'INDEX_BANKS',
  INDEX_BANKS_AND_FINANCIAL_SERVICES = 'INDEX_BANKS_AND_FINANCIAL_SERVICES',
  INDEX_CONSUMPTION = 'INDEX_CONSUMPTION',
  INDEX_DEFENCE = 'INDEX_DEFENCE',
  INDEX_FACTORAL_ALPHA = 'INDEX_FACTORAL_ALPHA',
  INDEX_FACTORAL_LOW_VOLATILITY = 'INDEX_FACTORAL_LOW_VOLATILITY',
  INDEX_FACTORAL_MOMENTUM = 'INDEX_FACTORAL_MOMENTUM',
  INDEX_FACTORAL_MULTIFACTOR = 'INDEX_FACTORAL_MULTIFACTOR',
  INDEX_FACTORAL_QUALITY = 'INDEX_FACTORAL_QUALITY',
  INDEX_FACTORAL_VALUE = 'INDEX_FACTORAL_VALUE',
  INDEX_HOUSING = 'INDEX_HOUSING',
  INDEX_INFRASTRUCTURE = 'INDEX_INFRASTRUCTURE',
  INDEX_INTERNATIONAL = 'INDEX_INTERNATIONAL',
  INDEX_LARGE_AND_MID_CAP = 'INDEX_LARGE_AND_MID_CAP',
  INDEX_LARGE_CAP = 'INDEX_LARGE_CAP',
  INDEX_LARGE_CAP_EQUAL_WEIGHT = 'INDEX_LARGE_CAP_EQUAL_WEIGHT',
  INDEX_LARGE_CAP_NEXT_FIFTY = 'INDEX_LARGE_CAP_NEXT_FIFTY',
  INDEX_LOW_VOLATILITY = 'INDEX_LOW_VOLATILITY',
  INDEX_MANUFACTURING = 'INDEX_MANUFACTURING',
  INDEX_MICROCAP = 'INDEX_MICROCAP',
  INDEX_MOMENTUM = 'INDEX_MOMENTUM',
  INDEX_MULTIFACTOR = 'INDEX_MULTIFACTOR',
  INDEX_OTHERS = 'INDEX_OTHERS',
  INDEX_REALTY = 'INDEX_REALTY',
  INDEX_MID_CAP = 'INDEX_MID_CAP',
  INDEX_PHARMA_AND_HEALTHCARE = 'INDEX_PHARMA_AND_HEALTHCARE',
  INDEX_PSU = 'INDEX_PSU',
  INDEX_SMALL_CAP = 'INDEX_SMALL_CAP',
  INDEX_TECHNOLOGY = 'INDEX_TECHNOLOGY',
  INDEX_TOTAL_MARKET = 'INDEX_TOTAL_MARKET',
  INDEX_TOTAL_MARKET_EQUAL = 'INDEX_TOTAL_MARKET_EQUAL',
  INDEX_TOURISM = 'INDEX_TOURISM',
  INDEX_VALUE = 'INDEX_VALUE',
  SECTORAL_THEMATIC_AUTO = 'SECTORAL_THEMATIC_AUTO',
  SECTORAL_THEMATIC_COMMODITY = 'SECTORAL_THEMATIC_COMMODITY',
  SECTORAL_THEMATIC_CONSUMPTION = 'SECTORAL_THEMATIC_CONSUMPTION',
  SECTORAL_THEMATIC_DEFENCE = 'SECTORAL_THEMATIC_DEFENCE',
  SECTORAL_THEMATIC_ENERGY = 'SECTORAL_THEMATIC_ENERGY',
  SECTORAL_THEMATIC_ESG = 'SECTORAL_THEMATIC_ESG',
  SECTORAL_THEMATIC_EXPORT_AND_SERVICES = 'SECTORAL_THEMATIC_EXPORT_AND_SERVICES',
  SECTORAL_THEMATIC_FINANCIAL_SERVICES = 'SECTORAL_THEMATIC_FINANCIAL_SERVICES',
  SECTORAL_THEMATIC_FMCG = 'SECTORAL_THEMATIC_FMCG',
  SECTORAL_THEMATIC_HEALTHCARE = 'SECTORAL_THEMATIC_HEALTHCARE',
  SECTORAL_THEMATIC_HOUSING = 'SECTORAL_THEMATIC_HOUSING',
  SECTORAL_THEMATIC_INFRASTRUCTURE = 'SECTORAL_THEMATIC_INFRASTRUCTURE',
  SECTORAL_THEMATIC_IPO = 'SECTORAL_THEMATIC_IPO',
  SECTORAL_THEMATIC_MANUFACTURING = 'SECTORAL_THEMATIC_MANUFACTURING',
  SECTORAL_THEMATIC_MNC = 'SECTORAL_THEMATIC_MNC',
  SECTORAL_THEMATIC_OTHERS = 'SECTORAL_THEMATIC_OTHERS',
  SECTORAL_THEMATIC_PSU = 'SECTORAL_THEMATIC_PSU',
  SECTORAL_THEMATIC_QUANT = 'SECTORAL_THEMATIC_QUANT',
  SECTORAL_THEMATIC_TECHNOLOGY = 'SECTORAL_THEMATIC_TECHNOLOGY',
  SECTORAL_THEMATIC_TRANSPORT_AND_LOGISTICS = 'SECTORAL_THEMATIC_TRANSPORT_AND_LOGISTICS',
  COMMODITY_GOLD = 'COMMODITY_GOLD',
  COMMODITY_SILVER = 'COMMODITY_SILVER',
  COMMODITY_GOLD_AND_SILVER = 'COMMODITY_GOLD_AND_SILVER',
  GLOBAL_EMERGING_MARKETS = 'GLOBAL_EMERGING_MARKETS',
  GLOBAL_DIVERSIFIED_ALL_COUNTRIES = 'GLOBAL_DIVERSIFIED_ALL_COUNTRIES',
  GLOBAL_EUROPE = 'GLOBAL_EUROPE',
  GLOBAL_OTHERS_ACTIVE = 'GLOBAL_OTHERS_ACTIVE',
  GLOBAL_INNOVATION_AND_DISRUPTION = 'GLOBAL_INNOVATION_AND_DISRUPTION',
  GLOBAL_OTHERS_PASSIVE = 'GLOBAL_OTHERS_PASSIVE',
  GLOBAL_DEVELOPED_MARKETS = 'GLOBAL_DEVELOPED_MARKETS',
  GLOBAL_US_NASDAQ_100 = 'GLOBAL_US_NASDAQ_100',
  GLOBAL_US_OTHERS_PASSIVE = 'GLOBAL_US_OTHERS_PASSIVE',
  GLOBAL_US_DIVERSIFIED_EQUITY = 'GLOBAL_US_DIVERSIFIED_EQUITY'
}

/** Preference types with dedicated questionnaire chrome (themes / core flow). */
export const CORE_PREFERENCE_TYPE_IDS: ReadonlySet<PreferenceTypeId> = new Set([
  PreferenceTypeId.SILVER_AND_GOLD,
  PreferenceTypeId.GLOBAL_US,
  PreferenceTypeId.DEBT_AND_ARBITRAGE,
  PreferenceTypeId.DEBT,
  PreferenceTypeId.RETIREMENT,
  PreferenceTypeId.CHILDREN,
  PreferenceTypeId.ELSS_TAX_SAVINGS,
  PreferenceTypeId.SATELLITE
])

export const TO_BE_MAPPED_PREFERENCE_TYPE_IDS: ReadonlySet<PreferenceTypeId> = new Set([
  PreferenceTypeId.SILVER_AND_GOLD,
  PreferenceTypeId.ELSS_TAX_SAVINGS,
  PreferenceTypeId.RETIREMENT,
  PreferenceTypeId.CHILDREN
])

export interface PreferenceListApiResponse {
  preferenceList: PreferenceTypeApi[]
}

export interface PreferencesQuestionnairePreferenceTypeApiItem {
  id: PreferenceTypeId | string
  title: string
  groupName?: string | null
  fundCount?: number | null
  isNewPreference?: boolean | null
  portfolioShare?: number | null
  disclaimer?: string | null
  groups?: PreferenceGroupRefApi[] | null
  fundList?: PreferenceFundItem[] | null
  questions: QuestionnaireQuestionApi[]
  reviewResolver?: PreferenceReviewResolverItem[] | null
}

export interface PreferencesQuestionnaireApiResponse {
  questionnaireId: string
  status: QuestionnaireStatus
  preferenceList: PreferencesQuestionnairePreferenceTypeApiItem[]
}

export interface PreferenceTypeApi {
  id: PreferenceTypeId | string
  title: string
  isNewPreference?: boolean | null
  allocationPercent: number
  fundList?: PreferenceFundItem[] | null
  disclaimer?: string | null
}

/** Preference fund — same keys as backend (`currentValue`, `companyLogoUrl`). */
export type PreferenceFundItem = QuestionOptionFundItem

export interface PreferencesQuestionnaireListApiResponse {
  categories: PreferencesQuestionnaireCategoryApiResponse[]
}

export interface PreferencesQuestionnaireCategoryApiResponse {
  categoryId: string
  categoryTitle: string
  questions: Question[]
}

export type PreferencesQuestionApi = Question

export interface PreferenceQuestionnaireDataResponse {
  questionnaireId: string
  questionGroups: PreferenceQuestionGroup[]
  status: QuestionnaireStatus
}

export interface PreferenceReviewResolverItem {
  reviewCondition: string
  reviewCopy: string
  questionId?: string | null
  showFundCount?: boolean | null
}

export interface PreferenceQuestionGroup {
  groupId: string
  isNewPreference?: boolean
  questions: QuestionnaireQuestionApi[]
  reviewResolver?: PreferenceReviewResolverItem[]
}

export enum PreferencesClearAnswersMode {
  ALL = 'ALL',
  PREFERENCE_TYPE = 'PREFERENCE_TYPE',
  AFTER_THIS = 'AFTER_THIS'
}

export interface PreferencesClearAnswersOptions {
  preferenceTypeId?: string
  questionId?: string
}

export interface SubmitPreferencesApiRequest {
  questionnaireId: string
  questionInputs: SubmitQuestionInput[]
}

export interface SubmitPreferencesApiResponse {
  success: boolean
}
